import { compareSsn, formatDate, maskedSsn, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const yes = (value) => value === true || /^(Y|YES|TRUE|1)$/.test(upper(value))
const no = (value) => value === false || /^(N|NO|FALSE|0)$/.test(upper(value))
const nameCase = (value) => titleCase(value).replace(/(^|\s)([a-z])(?=\s|$)/g, (m, space, letter) => space + letter.toUpperCase())
const personName = (n) => nameCase([n?.First, n?.Middle, n?.Last, /^(MR|MRS|MS|DR)$/i.test(text(n?.Suffix)) ? '' : n?.Suffix].filter(Boolean).join(' ') || n?.Full)

const RANK = { danger: 3, owner: 2, similar: 1, other: 0 }

export const buildLinkedIdentity = (imposter, owner = {}) => {
    const identities = list(imposter?.AKAs?.Identity).filter(i => i && typeof i === 'object')
    const names = [...new Set(identities.map(i => personName(i?.Name)).filter(Boolean))]
    const dob = identities.map(i => i?.DOB).find(d => dateValue(d))
    const age = identities.map(i => Number(i?.Age)).find(a => a > 0)
    const ssns = []
    identities.forEach(i => {
        const info = i?.SSNInfo || {}
        const key = text(info.SSN)
        if (!key || ssns.some(s => s.key === key)) return
        const indicator = upper(i?.SubjectSSNIndicator)
        const match = indicator ? null : compareSsn(key, owner?.SSN)
        const isOwners = indicator === 'YES' || match === 'same'
        ssns.push({
            key, masked: maskedSsn(key), isOwners, similar: indicator === 'CLOSE' || match === 'prefix', invalid: no(info.Valid),
            issued: [titleCase(info.IssuedLocation), [info.IssuedStartDate?.Year, info.IssuedEndDate?.Year].filter(Boolean).join('-')].filter(Boolean).join(', '),
        })
    })
    const dead = identities.find(i => dateValue(i?.DOD) || Number(i?.AgeAtDeath) > 0 || yes(i?.Deceased))
    const usesOwners = ssns.some(s => s.isOwners)
    const similar = ssns.some(s => s.similar)
    const invalid = ssns.some(s => s.invalid)
    const level = invalid || dead ? 'danger' : usesOwners ? 'owner' : similar ? 'similar' : 'other'

    return {
        more: moreFromLexis(imposter, 'Imposters/Imposter[]'),
        level, rank: RANK[level], usesOwners, similar, invalid, deceased: Boolean(dead),
        title: names[0] || 'Name not given',
        facts: [dob ? `Born ${formatDate(dob)}` : age ? `Age ${age}` : 'No date of birth',
            `${ssns.length} ${ssns.length === 1 ? 'SSN' : 'SSNs'}`, names.length > 1 && `${names.length - 1} other ${names.length === 2 ? 'name' : 'names'}`]
            .filter(Boolean).join(' · '),
        chips: [
            invalid && { label: 'SSN not valid', tone: 'declined' },
            dead && { label: 'Deceased', tone: 'declined' },
            usesOwners && { label: "Uses the owner's SSN", tone: 'watch' },
            similar && { label: 'Similar SSN', tone: 'neutral' },
        ].filter(Boolean),
        details: [
            ['Other names', names.slice(1).join('; ')],
            ['Date of birth', formatDate(dob)],
            ['Age', age && !dob ? String(age) : ''],
            ['Date of birth marked correct by Lexis', identities.some(i => yes(i?.IsCorrectDOB)) ? 'Yes' : ''],
            ['Current name per Lexis', identities.some(i => yes(i?.IsCurrentName)) ? 'Yes' : ''],
            ['Died', dead ? [formatDate(dead.DOD), Number(dead.AgeAtDeath) > 0 && `at age ${dead.AgeAtDeath}`,
                [titleCase(dead.DeathCounty), dead.DeathState].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || 'Yes' : ''],
            ...ssns.map((s, n) => [ssns.length > 1 ? `SSN ${n + 1}` : 'SSN',
                [s.masked, s.isOwners ? "the owner's SSN" : s.similar ? "similar to the owner's SSN" : 'a different SSN',
                    s.invalid && 'not valid', s.issued && `issued in ${s.issued}`].filter(Boolean).join(' · ')]),
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
    }
}

export function buildLinkedIdentities(imposters, owner = {}) {
    const rows = list(imposters).filter(i => list(i?.AKAs?.Identity).length > 0).map(i => buildLinkedIdentity(i, owner))
        .sort((a, b) => b.rank - a.rank)
    return {
        rows,
        summary: {
            count: rows.length,
            usesOwners: rows.filter(r => r.usesOwners).length,
            invalid: rows.filter(r => r.invalid).length,
            deceased: rows.filter(r => r.deceased).length,
        },
    }
}
